import { useState } from 'react'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2, LogIn, UserPlus, Mail, Lock, Phone, FileText } from 'lucide-react'
import toast from 'react-hot-toast'
import { getInfo, login, cadastrar } from './lojaApi'
import LojaLayout from './LojaLayout'

type Modo = 'login' | 'cadastro'

export default function LojaLogin() {
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const redirect = params.get('redirect') || `/loja/${slug}`

  const [modo, setModo] = useState<Modo>('login')
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [nome, setNome] = useState('')
  const [telefone, setTelefone] = useState('')
  const [cpfCnpj, setCpfCnpj] = useState('')
  const [loading, setLoading] = useState(false)

  const { data: info } = useQuery({
    queryKey: ['loja-info', slug],
    queryFn: () => getInfo(slug),
    enabled: !!slug,
  })

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    try {
      if (modo === 'login') {
        await login(slug, email, senha)
        toast.success('Bem-vindo de volta!')
      } else {
        if (nome.trim().length < 3) throw new Error('Informe seu nome completo.')
        if (senha.length < 6) throw new Error('A senha precisa ter ao menos 6 caracteres.')
        await cadastrar(slug, {
          nome: nome.trim(),
          email: email.trim(),
          senha,
          telefone: telefone || undefined,
          cpf_cnpj: cpfCnpj || undefined,
        })
        toast.success('Conta criada com sucesso!')
      }
      navigate(redirect, { replace: true })
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Erro ao entrar'
      toast.error(typeof msg === 'string' ? msg : 'Erro ao entrar')
    } finally {
      setLoading(false)
    }
  }

  return (
    <LojaLayout info={info || null} slug={slug}>
      <div className="max-w-md mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="card card-body space-y-5">
          {/* Tabs */}
          <div className="grid grid-cols-2 bg-ink-100 rounded-md p-1 gap-1">
            <button
              type="button"
              onClick={() => setModo('login')}
              className={`py-2 rounded text-sm font-medium transition ${
                modo === 'login' ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-600'
              }`}
            >
              Entrar
            </button>
            <button
              type="button"
              onClick={() => setModo('cadastro')}
              className={`py-2 rounded text-sm font-medium transition ${
                modo === 'cadastro' ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-600'
              }`}
            >
              Criar conta
            </button>
          </div>

          <div>
            <h1 className="text-xl font-semibold text-ink-900">
              {modo === 'login' ? 'Entrar na loja' : 'Criar sua conta'}
            </h1>
            <p className="text-sm text-ink-500 mt-0.5">
              {modo === 'login'
                ? 'Use seu e-mail e senha para acessar.'
                : 'Leva menos de 1 minuto.'}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            {modo === 'cadastro' && (
              <div>
                <label className="label">Nome completo</label>
                <input
                  className="input"
                  placeholder="Seu nome"
                  value={nome}
                  onChange={e => setNome(e.target.value)}
                  required
                />
              </div>
            )}

            <div>
              <label className="label flex items-center gap-1"><Mail size={12} /> E-mail</label>
              <input
                className="input"
                type="email"
                placeholder="voce@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="label flex items-center gap-1"><Lock size={12} /> Senha</label>
              <input
                className="input"
                type="password"
                placeholder={modo === 'cadastro' ? 'Mínimo 6 caracteres' : ''}
                value={senha}
                onChange={e => setSenha(e.target.value)}
                minLength={modo === 'cadastro' ? 6 : undefined}
                required
              />
            </div>

            {modo === 'cadastro' && (
              <>
                <div>
                  <label className="label flex items-center gap-1"><Phone size={12} /> Telefone (opcional)</label>
                  <input
                    className="input"
                    placeholder="(88) 99999-8888"
                    value={telefone}
                    onChange={e => setTelefone(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label flex items-center gap-1"><FileText size={12} /> CPF ou CNPJ (opcional)</label>
                  <input
                    className="input"
                    placeholder="000.000.000-00"
                    value={cpfCnpj}
                    onChange={e => setCpfCnpj(e.target.value)}
                  />
                </div>
              </>
            )}

            <button type="submit" className="btn-primary w-full justify-center" disabled={loading}>
              {loading
                ? <Loader2 size={14} className="animate-spin" />
                : modo === 'login' ? <LogIn size={14} /> : <UserPlus size={14} />}
              {modo === 'login' ? 'Entrar' : 'Criar conta e entrar'}
            </button>
          </form>

          <div className="text-xs text-ink-500 text-center">
            <Link to={`/loja/${slug}`} className="hover:text-brand-700">
              ← Continuar navegando sem entrar
            </Link>
          </div>
        </div>
      </div>
    </LojaLayout>
  )
}
